import { cloneElement, MouseEvent, ReactElement, useEffect, useRef, useState } from "react";

type MouseHoverTooltipProps = {
  children: ReactElement<{
    [key: string]: any;
    onMouseEnter?: (e: MouseEvent) => void;
    onMouseLeave?: (e: MouseEvent) => void;
    onMouseMove?: (e: MouseEvent) => void;
  }>;
  content?: ReactElement | string;
};

const MouseHoverTooltip = (props: MouseHoverTooltipProps) => {
  const [visible, setVisible] = useState(false);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const lastVisiblePositionRef = useRef({ x: 0, y: 0 });
  const timeoutRef = useRef<any>(null);
  const lastMoveTimeRef = useRef(0);
  const mouseMoveCheckRef = useRef<any>(null);

  const showTooltip = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    timeoutRef.current = setTimeout(() => {
      setVisible(true);
    }, 500);
  };

  const hideTooltip = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }

    if (mouseMoveCheckRef.current) {
      clearTimeout(mouseMoveCheckRef.current);
      mouseMoveCheckRef.current = null;
    }

    setVisible(false);
  };

  const handleMouseMove = (e: MouseEvent) => {
    const updateX = e.clientX;
    const updateY = e.clientY;

    setPosition({ x: updateX, y: updateY });
    lastMoveTimeRef.current = Date.now();

    if (!visible) {
      return;
    }

    const deltaX = Math.abs(updateX - lastVisiblePositionRef.current.x);
    const deltaY = Math.abs(updateY - lastVisiblePositionRef.current.y);
    const distance = Math.hypot(deltaX, deltaY);

    if (distance < 20) {
      return;
    }

    setVisible(false);

    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }

    if (mouseMoveCheckRef.current) {
      clearTimeout(mouseMoveCheckRef.current);
    }

    mouseMoveCheckRef.current = setTimeout(() => {
      const timeSinceLastMove = Date.now() - lastMoveTimeRef.current;

      if (timeSinceLastMove >= 200) {
        lastVisiblePositionRef.current = { x: updateX, y: updateY };
        showTooltip();
      } else {
        mouseMoveCheckRef.current = setTimeout(() => {
          lastVisiblePositionRef.current = { x: updateX, y: updateY };
          showTooltip();
        }, 150);
      }
    }, 200);
  };

  const handleMouseEnter = (e: MouseEvent) => {
    lastMoveTimeRef.current = Date.now();
    lastVisiblePositionRef.current = { x: e.clientX, y: e.clientY };
    showTooltip();
  };

  const handleMouseLeave = () => {
    hideTooltip();
  };

  useEffect(
    () => () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }

      if (mouseMoveCheckRef.current) {
        clearTimeout(mouseMoveCheckRef.current);
      }
    },
    []
  );

  const childProps = props.children.props;

  const clonedChild = cloneElement(props.children, {
    "data-tooltip-container": true,
    onMouseEnter: (e: MouseEvent) => {
      handleMouseEnter(e);

      if (childProps.onMouseEnter) {
        childProps.onMouseEnter(e);
      }
    },
    onMouseLeave: (e: MouseEvent) => {
      handleMouseLeave();

      if (childProps.onMouseLeave) {
        childProps.onMouseLeave(e);
      }
    },
    onMouseMove: (e: MouseEvent) => {
      handleMouseMove(e);

      if (childProps.onMouseMove) {
        childProps.onMouseMove(e);
      }
    },
  });

  return (
    <>
      {clonedChild}
      {visible && !!props.content && (
        <div
          style={{
            backgroundColor: "white",
            border: "1px solid #d4d4d5",
            borderRadius: "4px",
            boxShadow: "0 2px 4px 0 rgba(34,36,38,.12),0 2px 10px 0 rgba(34,36,38,.15)",
            fontSize: "13px",
            left: `${position.x - 10}px`,
            lineHeight: 1.25,
            maxWidth: "17.5rem",
            padding: "8px 12px",
            pointerEvents: "none",
            position: "fixed",
            top: `${position.y + 25}px`,
            zIndex: 9999,
          }}
        >
          <div
            style={{
              borderBottom: "6px solid white",
              borderLeft: "6px solid transparent",
              borderRight: "6px solid transparent",
              height: 0,
              left: "8px",
              position: "absolute",
              top: "-6px",
              width: 0,
            }}
          />
          {props.content}
        </div>
      )}
    </>
  );
};

export default MouseHoverTooltip;
